import { AxiosRequestConfig } from "axios";
import { userRoles } from "../../../constants";
import api from "../api";

// API path to the roles service
const path = "roleservice";

/**
 * Get roles for current user
 */
const getRolesForCurrentUser = async () => {
	const res = await api.request({
		method: "GET",
		url: `${path}/getrolesforcurrentuser`,
	});
	return { matches: res.data.matches, username: res.data.username };
};

/**
 * Get roles for current manager if user is manager
 */
const getRolesForCurrentManager = async () => {
	const res = await api.request({
		method: "GET",
		url: `${path}/getrolesforcurrentmanager`,
	});
	return { matches: res.data.matches, username: res.data.username };
};

/**
 * Get all users for manager roles
 */
const getUsersForManagerRoles = async () => {
	const res = await api.request({
		method: "GET",
		url: `${path}/getusersformanagerroles`,
	});
	return res.data.matches;
};

/**
 * Get all users from a tenant
 */
const getAllUsers = async () => {
	const res = await api.request({
		method: "GET",
		url: `${path}/users`,
	});
	return res.data.matches.map((user: { username: string }) => {
		return user.username;
	});
};

/**
 * Get all roles from a tenant
 */
const getAllRoles = async () => {
	const res = await api.request({
		method: "GET",
		url: `${path}/roles`,
	});
	return res.data.matches.sort((a: { name: string }, b: { name: string }) => {
		return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
	});
};

/**
 * Get a single role
 *
 * @param role role name
 */
const getSingleRole = async (role: string) => {
	const res = await api.request({
		method: "GET",
		url: `${path}/roles/${role}`,
	});
	return res.data.data;
};

/**
 * Add a new role
 *
 * @param data object with name and description of new role
 */
const addRole = async (
	data: {
		name: string;
		description: string;
		users: string[];
		managers: string[];
	},
	customOpts?: AxiosRequestConfig["customOpts"]
) => {
	const { name, description, users, managers } = data;
	await api.request({
		method: "POST",
		url: `${path}/roles`,
		data: { name, description, users, managers },
		customOpts,
	});
};

/**
 * Delete a role
 *
 * @param role role name to be deleted
 */
const deleteRole = async (
	role: string,
	customOpts?: AxiosRequestConfig["customOpts"]
) =>
	await api.request({
		method: "DELETE",
		url: `${path}/roles/${role}`,
		customOpts,
	});

/**
 * Update a role
 *
 * @param data object with role to be updated and name and description to be updated to
 */
const updateRole = async (
	data: {
		role: string;
		name?: string;
		description?: string;
	},
	customOpts?: AxiosRequestConfig["customOpts"]
) => {
	const { role, name, description } = data;
	await api.request({
		method: "PATCH",
		url: `${path}/roles/${role}`,
		data: { ...(name && { name }), ...(description && { description }) },
		customOpts,
	});
};

/**
 * Add user to a role
 *
 * @param data object with role and username
 */
const addUserToRole = async (
	data: { role: string; username: string },
	customOpts?: AxiosRequestConfig["customOpts"]
) => {
	const { role, username } = data;
	await api.request({
		method: "PATCH",
		url: `${path}/addusertorole`,
		data: { role, username },
		customOpts,
	});
};

/**
 * Add user as manager to a role
 *
 * @param data object with role and username
 */
const addManagerToRole = async (
	data: { role: string; username: string },
	customOpts?: AxiosRequestConfig["customOpts"]
) => {
	const { role, username } = data;
	await api.request({
		method: "PATCH",
		url: `${path}/addmanagertorole`,
		data: { role, username },
		customOpts,
	});
};

/**
 * Remove user from a role
 *
 * @param data object with role and username to be removed from role
 */
const removeUserFromRole = async (
	data: { role: string; username: string },
	customOpts?: AxiosRequestConfig["customOpts"]
) => {
	const { role, username } = data;
	await api.request({
		method: "DELETE",
		url: `${path}/removeuserfromrole/${username}/${role}`,
		customOpts,
	});
};

/**
 * Remove manager from a role
 *
 * @param data object with role and username to be removed from role
 */
const removeManagerFromRole = async (
	data: { role: string; username: string },
	customOpts?: AxiosRequestConfig["customOpts"]
) => {
	const { role, username } = data;
	await api.request({
		method: "DELETE",
		url: `${path}/removemanagerfromrole/${username}/${role}`,
		customOpts,
	});
};

/**
 * Get is user a form designer
 */
const getIsUserFormDesigner = async () => {
	const res = await getRolesForCurrentUser();
	const roles = res.matches.map((role: { name: string }) => role.name);

	return roles.includes(userRoles.FORM_DESIGNER);
};

export default {
	getRolesForCurrentUser,
	getRolesForCurrentManager,
	getUsersForManagerRoles,
	getAllUsers,
	getAllRoles,
	getSingleRole,
	addRole,
	addManagerToRole,
	deleteRole,
	removeManagerFromRole,
	updateRole,
	addUserToRole,
	removeUserFromRole,
	getIsUserFormDesigner,
};
